import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  ChevronDownIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { getHseDocuments } from "../../../api/hse/documents";
import { SelectorListSkeleton } from "../ResourceLoading";
import { PMDialog } from "../PMDialog";
import { useAutofocusOnOpen } from "../useAutofocusOnOpen";

const mapDocumentToItem = (row) => ({
  id: row.id,
  value: row.id,
  primary: row.title || `#${row.id}`,
  secondary: row.full_document_number || row.document_number || null,
  object: row,
});

const dedupeById = (rows) => {
  const seen = new Set();
  return rows.filter((row) => {
    if (!row?.id || seen.has(row.id)) return false;
    seen.add(row.id);
    return true;
  });
};

const itemMatchesQuery = (item, query) => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;

  return [item.primary, item.secondary, item.object?.description]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(needle));
};

export const HseDocumentSelector = ({
  showField = true,
  setFieldValue,
  values,
  onChange,
  errors,
  fieldVariant = "default",
  noLabel = false,
  ...props
}) => {
  const name = props?.name ?? "hse_document_id";
  const label = props?.label ?? "resource_hub.faq.link_type.hse_document";
  const noneLabelKey = props.noneLabelKey ?? "hse_document_selector.none";

  const { t } = useTranslation();
  const nonePrimary = t(noneLabelKey, { defaultValue: "Ingen dokument valgt" });

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [recentItems, setRecentItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchInputRef = useAutofocusOnOpen(open);
  const [selected, setSelected] = useState({
    id: null,
    field1: nonePrimary,
    field2: null,
  });

  useEffect(() => {
    if (props?.selected?.id != null && props?.selected?.id !== "") {
      setSelected({
        id: props.selected.id,
        field1: props.selected.displayTitle || props.selected.title || String(props.selected.id),
        field2: props.selected.document_number || props.selected.full_document_number || null,
      });
    } else {
      setSelected({
        id: null,
        field1: nonePrimary,
        field2: null,
      });
    }
  }, [props.selected, nonePrimary]);

  const handleOpen = () => {
    if (props.disabled) return;
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setQuery("");
    props.onModalClose?.();
  };

  const propagateSelection = (item) => {
    setSelected({
      id: item.id,
      field1: item.primary ?? nonePrimary,
      field2: item.secondary ?? null,
    });

    if (values && setFieldValue) {
      setFieldValue(name, item.id != null ? item.id : null);
    }
    if (onChange) {
      onChange({
        target: {
          name,
          value: item.id,
          data: item,
          hse_document: item.object,
        },
      });
    }
    handleClose();
  };

  const fetchItems = useCallback(async (searchQuery) => {
    setLoading(true);
    try {
      const body = await getHseDocuments({
        search: searchQuery?.trim() || undefined,
        include_fillable_forms: true,
      });
      const docs = Array.isArray(body?.documents) ? body.documents : [];
      const mapped = dedupeById(docs).map(mapDocumentToItem);

      if (!searchQuery?.trim()) {
        setRecentItems(mapped.slice(0, 12));
        setItems(mapped);
      } else {
        setItems(mapped);
      }
    } catch {
      setItems([]);
      if (!searchQuery?.trim()) setRecentItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedFetch = useMemo(() => debounce((q) => fetchItems(q), 300), [fetchItems]);

  useEffect(() => {
    if (!open) return undefined;
    debouncedFetch(query);
    return () => debouncedFetch.cancel();
  }, [query, open, debouncedFetch]);

  const visibleSections = useMemo(() => {
    const recent = recentItems.filter((item) => itemMatchesQuery(item, query));
    const recentIds = new Set(recent.map((item) => item.id));
    const results = items
      .filter((item) => itemMatchesQuery(item, query))
      .filter((item) => !recentIds.has(item.id));

    return [
      {
        key: "recent",
        title: t("hse_document_selector.recent", { defaultValue: "Siste dokumenter" }),
        icon: ClockIcon,
        rows: recent,
      },
      {
        key: "results",
        title: query.trim()
          ? t("hse_document_selector.search_results", { defaultValue: "Søkeresultater" })
          : t("hse_document_selector.all_documents", { defaultValue: "Alle dokumenter" }),
        icon: null,
        rows: results,
      },
    ].filter((section) => section.rows.length > 0);
  }, [items, query, recentItems, t]);

  const hasSelection = selected?.id != null;
  const isCompactField = fieldVariant === "compact";
  const labelClassName = isCompactField
    ? "mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500"
    : "mb-1 block text-sm font-medium text-gray-700";
  const triggerClassName = [
    "relative flex w-full items-center justify-between",
    "rounded-md border bg-white px-3 py-2 text-sm shadow-sm",
    "transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500",
    isCompactField ? "min-h-[38px] text-gray-900" : "",
    props.disabled
      ? "cursor-not-allowed border-gray-200 text-gray-400"
      : hasSelection
      ? isCompactField
        ? "border-gray-300 text-gray-900 hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        : "border-blue-400 text-gray-900 hover:border-blue-500"
      : "border-gray-300 text-gray-500 hover:border-gray-400",
  ]
    .filter(Boolean)
    .join(" ");

  const renderItem = (item) => {
    const isSelected = item.id === selected?.id;
    return (
      <li
        key={item.id ?? "none"}
        onClick={() => propagateSelection(item)}
        className={[
          "flex cursor-pointer items-center justify-between px-4 py-3 transition-colors",
          isSelected ? "bg-blue-50 text-blue-700" : "text-gray-900 hover:bg-gray-50",
        ].join(" ")}
      >
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{item.primary}</p>
          {item.secondary ? (
            <p className="mt-0.5 truncate text-xs text-gray-500">{item.secondary}</p>
          ) : null}
        </div>
        {isSelected ? <CheckIcon className="ml-3 h-4 w-4 flex-shrink-0 text-blue-600" /> : null}
      </li>
    );
  };

  const triggerButton =
    showField !== false ? (
      <div>
        {!noLabel && label !== false ? (
          <label className={labelClassName}>{t(label)}</label>
        ) : null}
        <button
          type="button"
          disabled={props.disabled}
          onClick={handleOpen}
          className={triggerClassName}
        >
          <div className="min-w-0 flex-1 text-left">
            <span className="block truncate">{selected.field1}</span>
            {!isCompactField && selected.field2 ? (
              <span className="mt-0.5 block truncate text-xs text-gray-400">{selected.field2}</span>
            ) : null}
          </div>
          <ChevronDownIcon className="ml-2 h-4 w-4 flex-shrink-0 text-gray-400" />
        </button>
        {errors?.[name] ? <p className="mt-1 text-xs text-red-600">{errors[name]}</p> : null}
      </div>
    ) : null;

  const dialogContent = (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex-shrink-0 border-b border-gray-200 p-3 sm:p-4">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">{t(label)}</h3>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-md p-1.5 text-gray-400 transition-colors hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
          </div>
          <input
            ref={searchInputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("hse_document_selector.search_placeholder", {
              defaultValue: "Søk etter HMS-dokument...",
            })}
            className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-9 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute inset-y-0 right-0 flex items-center pr-3"
            >
              <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
            </button>
          ) : null}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {loading ? (
          <SelectorListSkeleton />
        ) : (
          <div>
            <ul className="divide-y divide-gray-100">
              {renderItem({
                id: null,
                value: null,
                primary: nonePrimary,
                secondary: null,
              })}
            </ul>
            {visibleSections.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                {t("hse_document_selector.no_documents", { defaultValue: "Ingen dokumenter funnet" })}
              </div>
            ) : (
              visibleSections.map((section) => {
                const SectionIcon = section.icon;
                return (
                  <div key={section.key}>
                    <div className="flex items-center gap-2 border-y border-gray-100 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase text-gray-600">
                      {SectionIcon ? <SectionIcon className="h-4 w-4" /> : null}
                      <span>{section.title}</span>
                    </div>
                    <ul className="divide-y divide-gray-100">
                      {section.rows.map((item) => renderItem(item))}
                    </ul>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {triggerButton}
      <PMDialog
        open={open}
        onClose={handleClose}
        size="md"
        dense
        closeOnOutsideClick
        zIndex={props.zIndex}
      >
        {dialogContent}
      </PMDialog>
    </>
  );
};

export default HseDocumentSelector;
